import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import config from "config";
import i18n from "../i18n";

const SettingsContext = createContext();

const defaultSettings = {
  lang: "en",
  accept_newsletters: "0",
  number_format: "eu",
  show_decimals: "1",
  hide_amounts: "0",
  tips_enabled: "1",
  ai_enabled: "1",
  ai_tone: "professional",
  invert_balance_display: "0",
  balance_chart_interval: "week",
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(defaultSettings);
  const [isLoaded, setIsLoaded] = useState(false);

  const fetchSettings = async () => {
    try {
      // Skip on public pages (auth layout) or if no token
      if (!Cookies.get("loggedIn") || window.location.hash.startsWith("#/auth")) {
        setIsLoaded(true);
        return;
      }

      const response = await axios.get(`${config.apiUrl}/mySettings`);

      if (response.status === 200 && response.data) {
        const newSettings = { ...defaultSettings, ...response.data };
        setSettings(newSettings);
        
        // Apply language setting
        if (newSettings.lang && newSettings.lang !== i18n.language) {
          i18n.changeLanguage(newSettings.lang);
        }
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setIsLoaded(true);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    
    // Apply language change immediately
    if (key === "lang") {
      i18n.changeLanguage(value);
    }
  };

  const updateSettings = (newSettings) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  };

  return (
    <SettingsContext.Provider value={{ 
      settings, 
      updateSetting, 
      updateSettings, 
      isLoaded,
      refetchSettings: fetchSettings 
    }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
};

export default SettingsContext;
